// PurchaseOrderPreviewModal.jsx

import React, { useCallback, useMemo, useRef } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Utilities
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { openPrintDocument } from 'utilities/Print';
import { extractRichTextPlainText } from 'utilities/RichText';
import { DOCUMENT_PREVIEW_PRINT_STYLES } from 'nodetypes/documents/kit/documentPreviewPrintStyles';
import {
  PURCHASE_ORDER_TYPE_KEY,
  readPurchaseOrderField,
} from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';

// CSS
import './PurchaseOrderPreviewModal.css';

// Components
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';
import ModalButtonSection from 'components/modal/buttonsection/ModalButtonSection';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import PurchaseOrderGeneralInfo from 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo';
import PurchaseOrderMemo from 'nodetypes/purchaseorder/view/PurchaseOrderMemo';
import PurchaseOrderPreviewItems from 'nodetypes/purchaseorder/view/PurchaseOrderPreviewItems';
import PurchaseOrderTerms from 'nodetypes/purchaseorder/view/PurchaseOrderTerms';

function cleanString(value) {
  return String(value || '').trim();
}

function hasRichTextContent(node, fieldName, nodeType) {
  const typeKey = cleanString(nodeType?.key || node?.type || PURCHASE_ORDER_TYPE_KEY).toUpperCase();
  const value = readPurchaseOrderField(node, fieldName, nodeType);
  if (value !== undefined) {
    return Boolean(extractRichTextPlainText(value));
  }
  const fallback = readNodeFieldRawValue(
    node,
    { name: fieldName, source: typeKey.toLowerCase() },
    typeKey,
  );
  return Boolean(extractRichTextPlainText(fallback));
}

/**
 * PurchaseOrderPreviewModal
 *
 * Designer-only preview of the vendor-facing purchase order document.
 *
 * @param {boolean} isOpen
 * @param {function} onClose
 * @param {object|null} node
 * @param {object|null} [nodeType]
 * @param {object[]} [neighborNodes]
 * @param {object|null} [letterheadOverride]
 * @param {object|null} [relatedNodesByKey]
 * @returns {ReactNode}
 */
const PurchaseOrderPreviewModal = React.memo(({
  isOpen = false,
  onClose,
  node = null,
  nodeType = null,
  neighborNodes = [],
  letterheadOverride = null,
  relatedNodesByKey = null,
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- REFS ----- //

  const sheetRef = useRef(null);

  // ----- MEMOS ----- //

  const printTitle = useMemo(() => {
    const title = cleanString(node?.title);
    const number = cleanString(
      readPurchaseOrderField(node, 'number', nodeType)
      || readPurchaseOrderField(node, 'poNumber', nodeType),
    );
    if (title && number) return `${title} (${number})`;
    return title || number || 'Purchase Order Preview';
  }, [node, nodeType]);

  const showTerms = useMemo(
    () => hasRichTextContent(node, 'termsAndConditions', nodeType),
    [node, nodeType],
  );
  const showMemo = useMemo(
    () => hasRichTextContent(node, 'memo', nodeType),
    [node, nodeType],
  );

  // ----- HANDLERS ----- //

  /**
   * Opens the browser print dialog for the preview sheet.
   *
   * @returns {void}
   */
  const handlePrint = useCallback(() => {
    const markup = sheetRef.current?.outerHTML;
    if (!markup) return;
    openPrintDocument({
      bodyMarkup: markup,
      styles: DOCUMENT_PREVIEW_PRINT_STYLES,
      title: printTitle,
    });
  }, [printTitle]);

  if (!isOpen || !node) return null;

  // ----- RENDER ----- //

  return (
    <Modal
      title="Purchase Order Preview"
      iconKey="visibility"
      isOpen={isOpen}
      onClose={onClose}
      width={isMobile ? '100%' : '960px'}
      maxHeight={isMobile ? '100%' : '90vh'}
      padding="0"
    >
      <ModalForm>
        <div
          ref={sheetRef}
          className="purchase-order-preview-modal-body"
        >
          <section className="purchase-order-preview-modal-section">
            <PurchaseOrderGeneralInfo
              node={node}
              nodeType={nodeType}
              readOnly
              letterheadOverride={letterheadOverride}
              relatedNodesByKey={relatedNodesByKey}
            />
          </section>

          <section className="purchase-order-preview-modal-section">
            <PurchaseOrderPreviewItems
              node={node}
              nodeType={nodeType}
              neighborNodes={neighborNodes}
            />
          </section>

          {showTerms ? (
            <section className="purchase-order-preview-modal-section">
              <PurchaseOrderTerms
                node={node}
                nodeType={nodeType}
                readOnly
              />
            </section>
          ) : null}

          {showMemo ? (
            <section className="purchase-order-preview-modal-section">
              <PurchaseOrderMemo
                node={node}
                nodeType={nodeType}
                readOnly
              />
            </section>
          ) : null}
        </div>
      </ModalForm>

      <ModalButtons>
        <ModalButtonSection align="START">
          <Button
            label="Print"
            iconKey="print"
            onClick={handlePrint}
          />
        </ModalButtonSection>
        <ModalButtonSection align="END">
          <Button
            label="Close"
            onClick={onClose}
          />
        </ModalButtonSection>
      </ModalButtons>
    </Modal>
  );
});

// Dev Tools
PurchaseOrderPreviewModal.displayName = 'PurchaseOrderPreviewModal';

export default PurchaseOrderPreviewModal;
